import React, { useState, useEffect } from 'react';
import backgroundImage from '../../assets/background.png';
import BookingModal from './BookingModal';

import { API_BASE } from '../../config/runtime';

const formatPeriod = (startDate, endDate) => {
  if (startDate && endDate) return `${startDate} - ${endDate}`;
  if (startDate) return startDate;
  if (endDate) return endDate;
  return 'TBD';
};

/* ─── Reusable promo card ──────────────────────────────────────────────── */
const PromoCard = ({ promo, getBranchLabel, onReserve, compact = false }) => (
  <div className={`relative overflow-hidden rounded-2xl border border-white/25 bg-white/10 shadow-[0_24px_60px_rgba(12,10,8,0.32)] backdrop-blur-lg ${compact ? 'px-4 py-3' : 'px-6 py-5'}`}>
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.4),transparent_62%)]" />
    <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.18),transparent_40%,rgba(255,255,255,0.08))]" />

    <div className="relative">
      <p className="text-[10px] font-semibold uppercase tracking-[0.34em] text-[#f1e7df]">Exclusive Offer</p>
    </div>

    <div className="relative mt-2.5 rounded-[14px] border border-white/30 bg-white/10 px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate">{promo?.name}</p>
          <p className="mt-0.5 text-[10px] uppercase tracking-[0.26em] text-white/70">
            {formatPeriod(promo?.startDate, promo?.endDate)}
          </p>
        </div>
        <span className="flex-shrink-0 rounded-full border border-white/30 bg-white/15 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/90">
          {promo?.discount || 'Special offer'}
        </span>
      </div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="rounded-full border border-white/35 bg-white/15 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/90">
          {promo?.promo_code || promo?.code || 'PROMO'}
        </span>
        <span className="text-[10px] font-semibold text-white/80 whitespace-nowrap">Limited time</span>
      </div>

      <p className="mt-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70">
        {getBranchLabel(promo)}
      </p>

      {promo?.description && (
        <p className={`mt-2 text-[11px] leading-relaxed text-white/80 ${compact ? 'line-clamp-2' : ''}`}>
          {promo.description}
        </p>
      )}
    </div>

    <div className="relative mt-2.5">
      <button
        type="button"
        onClick={onReserve}
        className="text-[11px] uppercase tracking-[0.24em] text-white/80 hover:text-white transition"
      >
        Reserve Now
      </button>
    </div>
  </div>
);

const Hero = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [promos, setPromos] = useState([]);
  const [isLoadingPromos, setIsLoadingPromos] = useState(true);
  const [promoError, setPromoError] = useState('');
  const [promoIndex, setPromoIndex] = useState(0);
  const [isAdVisible, setIsAdVisible] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedPromo, setSelectedPromo] = useState(null);

  const user = JSON.parse(localStorage.getItem('user')) || null;
  const isLoggedIn = user && user.role?.toLowerCase() === 'client';

  useEffect(() => {
    const handleScroll = () => {
      const progress = Math.min(window.scrollY / 500, 1);
      setScrollProgress(progress);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isBookingModalOpen) {
      const fetchServices = async () => {
        try {
          const response = await fetch(`${API_BASE}/get_services.php`);
          const data = await response.json();
          if (data.success) setServices(data.services || []);
        } catch (error) {
          console.error('Error fetching services:', error);
          setServices([]);
        }
      };
      fetchServices();
    }
  }, [isBookingModalOpen]);

  useEffect(() => {
    const fetchPromos = async () => {
      setIsLoadingPromos(true);
      setPromoError('');
      try {
        const response = await fetch(`${API_BASE}/get_promos.php`);
        const data = await response.json();
        if (data.success) {
          const livePromos = (Array.isArray(data.promos) ? data.promos : []).filter(
            (p) => p.status === 'Live'
          );
          setPromos(livePromos);
        } else {
          setPromoError(data.message || 'Unable to load promotions.');
        }
      } catch (error) {
        setPromoError('Unable to load promotions: ' + error.message);
      } finally {
        setIsLoadingPromos(false);
      }
    };
    fetchPromos();
  }, []);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await fetch(`${API_BASE}/get_branches.php`);
        const data = await response.json();
        if (data.success) setBranches(Array.isArray(data.branches) ? data.branches : []);
      } catch {
        setBranches([]);
      }
    };
    fetchBranches();
  }, []);

  const getBranchLabel = (promo) => {
    const ids = Array.isArray(promo?.branches) ? promo.branches : [];
    if (!ids.length) return 'All branches';
    const names = ids
      .map((id) => branches.find((b) => Number(b.branch_id ?? b.id) === Number(id))?.branch_name)
      .filter(Boolean);
    return names.length ? names.join(', ') : 'Selected branches';
  };

  useEffect(() => {
    if (isLoadingPromos || promoError || promos.length === 0) {
      setIsAdVisible(false);
      return;
    }
    let isMounted = true;
    let hideTimer;

    const showAd = () => {
      if (!isMounted) return;
      setIsAdVisible(true);
      hideTimer = setTimeout(() => {
        if (!isMounted) return;
        setIsAdVisible(false);
      }, 5000);
    };

    showAd();
    const rotateTimer = setInterval(() => {
      if (!isMounted) return;
      setPromoIndex((prev) => (prev + 1) % promos.length);
      showAd();
    }, 8000);

    return () => {
      isMounted = false;
      clearTimeout(hideTimer);
      clearInterval(rotateTimer);
    };
  }, [isLoadingPromos, promoError, promos]);

  const hasPromos = !promoError && !isLoadingPromos && promos.length > 0;
  const currentPromo = promos[promoIndex];

  const handleBookClick = () => {
    setSelectedPromo(null);
    setIsBookingModalOpen(true);
  };

  const handlePromoReserve = () => {
    setSelectedPromo(currentPromo || null);
    setIsBookingModalOpen(true);
  };

  const handleBookingComplete = () => {
    setSelectedServices([]);
    setSelectionMode(false);
    setSelectedPromo(null);
  };

  return (
    <>
      <div
        className="relative w-full overflow-hidden transition-shadow duration-300 min-h-[600px] h-svh"
        style={{
          borderBottomLeftRadius: `${scrollProgress * 80}px`,
          borderBottomRightRadius: `${scrollProgress * 80}px`,
          boxShadow: `0 10px ${20 * scrollProgress}px -10px rgba(0,0,0,${0.2 * scrollProgress})`,
        }}
      >
        {/* Background */}
        <img
          src={backgroundImage}
          alt="Spa Treatment"
          className="absolute inset-0 w-full h-full object-cover object-center z-0 brightness-50"
        />

        {/* Hero content — on mobile: flex column fills height, pushes promo below button */}
        <div className="relative z-10 h-full flex flex-col justify-end items-start text-left px-5 pb-5 sm:px-10 sm:pb-24 md:px-16 md:pb-28 lg:px-20 lg:pb-32">
          <h1 className="font-serif text-white mb-6 leading-tight drop-shadow-lg text-[clamp(2.25rem,7vw,4.5rem)] max-w-full sm:max-w-[36rem]">
            Treat your body <br /> Ease your soul
          </h1>

          <button
            onClick={handleBookClick}
            className="w-full max-w-[400px] sm:w-auto bg-white text-[#4A4A4A] rounded-full font-bold hover:bg-[#EAE3DB] transition-all uppercase tracking-wider shadow-md px-6 py-3.5 text-xs sm:px-10 sm:py-4 sm:text-sm"
          >
            Book an Appointment
          </button>

          {/* Mobile-only promo — rendered in flow below the button so it never overlaps */}
          {hasPromos && (
            <div
              className={`
                sm:hidden w-full mt-4 transition-all duration-700
                ${isAdVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}
              `}
            >
              <PromoCard
                promo={currentPromo}
                getBranchLabel={getBranchLabel}
                onReserve={handlePromoReserve}
                compact
              />
            </div>
          )}
        </div>

        {/* Desktop-only promo — absolutely positioned bottom-right, never overlaps mobile */}
        {hasPromos && (
          <div
            className={`
              hidden sm:block absolute z-10 transition-all duration-700
              ${isAdVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'}
              right-6 bottom-8 w-[clamp(280px,28vw,400px)]
              lg:right-10 lg:bottom-10
            `}
          >
            <PromoCard
              promo={currentPromo}
              getBranchLabel={getBranchLabel}
              onReserve={handlePromoReserve}
            />
          </div>
        )}
      </div>

      <BookingModal
        isOpen={isBookingModalOpen}
        onClose={() => {
          setIsBookingModalOpen(false);
          setSelectedPromo(null);
        }}
        selectedServices={selectedServices}
        user={user}
        isLoggedIn={isLoggedIn}
        onBookingComplete={handleBookingComplete}
        selectedPromo={selectedPromo}
        services={services}
        onServicesChange={setSelectedServices}
        selectionMode={selectionMode}
        onSelectionModeChange={setSelectionMode}
      />
    </>
  );
};

export default Hero;
